import * as React from 'react';
import { BuildingIcon, PlusCircleIcon, ExclamationCircleIcon, CheckCircleIcon, RocketIcon, MagicIcon, MapMarkedAltIcon, GlobeIcon } from "@patternfly/react-icons";
import { getOrganizations, getAdsByOrganizationId, getCampaignsByOrganizationId, deleteOrganization } from "@app/utils/utils";
import { useEffect, useState } from "react";
import { RegisterOrg } from "@app/Organizations/RegisterOrg";
import { EditOrganization } from "@app/Organizations/EditOrganization";
import { OrganizationHeroCard } from "@app/Organizations/OrganizationHeroCard";
import { OrganizationCard } from "@app/Organizations/OrganizationCard";
import { PageSection, EmptyState, EmptyStateIcon, EmptyStateBody, EmptyStateVariant, Title } from "@patternfly/react-core";
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { colors, radii } from '@app/components/designTokens';
import './organizations.css';

const Organizations: React.FunctionComponent<any> = (props) => {

    const [organizations, setOrganizations] = useState<any[]>([]);
    const [triggerRefresh, setTriggerRefresh] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);
    const [orgStats, setOrgStats] = useState<Record<string, { adsCount: number; campaignsCount: number }>>({});

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState<any>(null);

    // Error modal state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Welcome modal state
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    // Guided tour state
    const [showGuidedTour, setShowGuidedTour] = useState(false);
    // Onboarding step 2: after the first org is registered, highlight the
    // "Research My Business" button on the hero card until the user triggers it.
    const [highlightResearch, setHighlightResearch] = useState(false);

    const fetchOrganizations = () => {
        getOrganizations().then(function(result) {
            if (result && result.organizations && result.organizations.length > 0) {
                setOrganizations(result.organizations);
                setShowWelcomeModal(false);
            } else {
                setOrganizations([]);
                const hasSeenWelcome = sessionStorage.getItem('advolt_welcome_dismissed');
                if (!hasSeenWelcome) {
                    setShowWelcomeModal(true);
                }
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        setIsLoading(true);
        fetchOrganizations();
    }, [triggerRefresh]);

    // Poll for research status changes
    useEffect(() => {
        const hasInProgress = organizations.some(org => org.researchStatus === 'IN_PROGRESS');
        if (!hasInProgress) return;

        const interval = setInterval(() => {
            fetchOrganizations();
        }, 5000);

        return () => clearInterval(interval);
    }, [organizations]);

    const handleResearchTriggered = () => {
        // Immediate refresh + polling will start automatically
        fetchOrganizations();
    };

    const handleWelcomeGetStarted = () => {
        sessionStorage.setItem('advolt_welcome_dismissed', 'true');
        setShowWelcomeModal(false);
        // Enable guided tour to highlight the Register button
        setShowGuidedTour(true);
        // Add class to body to blur sidebar
        document.body.classList.add('guided-tour-active');
    };

    const handleGuidedTourClick = () => {
        // User clicked the highlighted button, end the tour
        setShowGuidedTour(false);
        // Remove class from body
        document.body.classList.remove('guided-tour-active');
    };

    // Continue the onboarding tour: once the user registers their first
    // organization, guide them to research their business on the hero card.
    // (The Plan-tab nudge comes next, after they kick off research — see
    // handleResearchTourAdvance.)
    const handleFirstOrgRegistered = () => {
        setHighlightResearch(true);
    };

    // Onboarding step 3: after the user kicks off business research, stop
    // highlighting that button and nudge them toward the Plan tab to choose a
    // subscription. The Plan tab lives in AppLayout, so we persist a flag and
    // fire an event it listens for (it's already mounted, so storage events
    // won't reach it).
    const handleResearchTourAdvance = () => {
        setHighlightResearch(false);
        sessionStorage.setItem('advolt_highlight_plan', 'true');
        window.dispatchEvent(new CustomEvent('advolt:highlight-plan'));
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            document.body.classList.remove('guided-tour-active');
        };
    }, []);

    const handleEditClick = (organization: any) => {
        setSelectedOrganization(organization);
        setEditModalOpen(true);
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
        setSelectedOrganization(null);
    };

    const handleEditSave = () => {
        setTriggerRefresh(!triggerRefresh);
    };

    const handleDeleteClick = async (organization: any) => {
        try {
            await deleteOrganization(organization.id);
            // Remove the deleted organization from local state immediately
            setOrganizations(prevOrgs => prevOrgs.filter(org => org.id !== organization.id));
        } catch (error) {
            console.error('Failed to delete organization:', error);
            setErrorMessage(`Failed to delete "${organization.organizationName}". Please try again.`);
            setShowErrorModal(true);
        }
    };

    const fetchOrgStats = async (orgId: string) => {
        const [ads, campaigns] = await Promise.all([
            getAdsByOrganizationId(orgId),
            getCampaignsByOrganizationId(orgId)
        ]);
        setOrgStats(prev => ({
            ...prev,
            [orgId]: {
                adsCount: ads ? ads.length : 0,
                campaignsCount: campaigns ? campaigns.length : 0
            }
        }));
    };

    const handleCardClick = (organization: any) => {
        setExpandedOrgId(organization.id);
        if (!orgStats[organization.id]) {
            fetchOrgStats(organization.id);
        }
    };

    const handleCollapse = () => {
        setExpandedOrgId(null);
    };

    // Auto-expand if only 1 org
    useEffect(() => {
        if (organizations.length === 1) {
            const org = organizations[0];
            setExpandedOrgId(org.id);
            if (!orgStats[org.id]) {
                fetchOrgStats(org.id);
            }
        }
    }, [organizations]);

    const styles = {
        container: {
            padding: '24px'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
        },
        title: {
            fontSize: '28px',
            fontWeight: 700,
            color: '#1f2937',
            margin: 0
        },
        subtitle: {
            fontSize: '14px',
            color: '#6b7280',
            marginTop: '4px'
        },
        expandedSection: {
            marginBottom: '32px'
        },
        compactGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
        },
        addOrgCard: {
            background: '#ffffff',
            borderRadius: '12px',
            border: '2px dashed #e5e7eb',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            minHeight: '120px'
        },
        addOrgIcon: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#667eea',
            fontSize: '24px'
        },
        addOrgText: {
            color: '#6b7280',
            fontSize: '14px',
            fontWeight: 500
        },
        emptyStateContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
        },
        emptyStateContent: {
            textAlign: 'center' as const,
            maxWidth: '400px'
        },
        emptyIcon: {
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            color: '#ffffff',
            fontSize: '36px'
        },
        emptyTitle: {
            fontSize: '24px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 12px 0'
        },
        emptyText: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 24px 0',
            lineHeight: 1.6
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            color: '#6b7280'
        },
        singleOrgRegisterButton: {
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'center'
        },
        secondaryButton: {
            background: 'transparent',
            border: '2px solid #667eea',
            color: '#667eea',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
        },
        // Error modal styles
        errorOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        },
        errorModal: {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        },
        errorIcon: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            color: '#dc2626'
        },
        errorTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 8px 0',
            textAlign: 'center' as const
        },
        errorText: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 20px 0',
            lineHeight: 1.5,
            textAlign: 'center' as const
        },
        errorButton: {
            width: '100%',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            cursor: 'pointer'
        }
    };

    const emptyStateStyles = {
        wrapper: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            padding: '40px 20px',
            minHeight: '500px'
        },
        heroSection: {
            textAlign: 'center' as const,
            marginBottom: '48px',
            maxWidth: '600px'
        },
        heroIcon: {
            width: '80px',
            height: '80px',
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '40px',
            color: '#ffffff',
            boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
            margin: '0 auto 28px'
        },
        mainTitle: {
            fontSize: '42px',
            fontWeight: 800,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: '0 0 16px 0',
            letterSpacing: '-1px',
            textShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
        },
        mainSubtitle: {
            fontSize: '18px',
            color: '#6b7280',
            margin: 0,
            lineHeight: 1.6
        },
        card: {
            background: '#ffffff',
            borderRadius: '24px',
            padding: '40px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
            border: '2px solid transparent'
        },
        cardHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '24px'
        },
        cardIconContainer: {
            width: '64px',
            height: '64px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '28px',
            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)'
        },
        cardHeaderText: {
            flex: 1
        },
        cardTitle: {
            fontSize: '22px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 4px 0'
        },
        cardSubtitle: {
            fontSize: '14px',
            color: '#9ca3af',
            margin: 0
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
            margin: '0 0 32px 0'
        },
        featureItem: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px 0',
            borderBottom: '1px solid #f3f4f6'
        },
        featureItemLast: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px 0'
        },
        featureIconContainer: {
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: '#f0fdf4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#10b981',
            flexShrink: 0
        },
        featureContent: {
            flex: 1
        },
        featureTitle: {
            fontSize: '15px',
            fontWeight: 600,
            color: '#1f2937',
            margin: '0 0 2px 0'
        },
        featureDescription: {
            fontSize: '13px',
            color: '#6b7280',
            margin: 0,
            lineHeight: 1.5
        },
    };

    const welcomeModalStyles = {
        body: { padding: '40px 32px 32px' } as React.CSSProperties,
        headerBlock: { textAlign: 'center' as const, marginBottom: '28px' },
        title: {
            color: colors.title,
            fontSize: '28px',
            fontWeight: 700,
            margin: 0,
            marginBottom: '8px',
            letterSpacing: '-0.5px'
        },
        subtitle: {
            color: colors.subtle,
            fontSize: '15px',
            margin: 0,
            lineHeight: 1.5
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
            margin: '0 0 24px 0'
        },
        featureItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '14px 0',
            borderBottom: `1px solid ${colors.border}`
        } as React.CSSProperties,
        featureItemLast: {
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '14px 0'
        } as React.CSSProperties,
        featureIcon: {
            width: '36px',
            height: '36px',
            borderRadius: radii.input,
            background: colors.accentRing,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.accent,
            fontSize: '16px',
            flexShrink: 0
        } as React.CSSProperties,
        featureText: {
            fontSize: '14px',
            fontWeight: 500,
            color: colors.body
        }
    };

    const renderEmptyState = () => (
        <div style={emptyStateStyles.wrapper}>
            {/* Registration Card */}
            <div style={emptyStateStyles.card}>
                <div style={emptyStateStyles.cardHeader}>
                    <div style={emptyStateStyles.cardIconContainer}>
                        <BuildingIcon />
                    </div>
                    <div style={emptyStateStyles.cardHeaderText}>
                        <h2 style={emptyStateStyles.cardTitle}>Register Your Organization</h2>
                        <p style={emptyStateStyles.cardSubtitle}>Quick setup in under 2 minutes</p>
                    </div>
                </div>

                <ul style={emptyStateStyles.featureList}>
                    <li style={emptyStateStyles.featureItem}>
                        <div style={emptyStateStyles.featureIconContainer}>
                            <CheckCircleIcon />
                        </div>
                        <div style={emptyStateStyles.featureContent}>
                            <h4 style={emptyStateStyles.featureTitle}>Create Targeted Campaigns</h4>
                            <p style={emptyStateStyles.featureDescription}>
                                Launch geo-fenced and route-based ad campaigns that reach the right audience.
                            </p>
                        </div>
                    </li>
                    <li style={emptyStateStyles.featureItem}>
                        <div style={emptyStateStyles.featureIconContainer}>
                            <CheckCircleIcon />
                        </div>
                        <div style={emptyStateStyles.featureContent}>
                            <h4 style={emptyStateStyles.featureTitle}>Upload or Generate Ads</h4>
                            <p style={emptyStateStyles.featureDescription}>
                                Use your own media assets or let AI create stunning visuals for you.
                            </p>
                        </div>
                    </li>
                    <li style={emptyStateStyles.featureItemLast}>
                        <div style={emptyStateStyles.featureIconContainer}>
                            <CheckCircleIcon />
                        </div>
                        <div style={emptyStateStyles.featureContent}>
                            <h4 style={emptyStateStyles.featureTitle}>Track Performance</h4>
                            <p style={emptyStateStyles.featureDescription}>
                                Monitor impressions, engagement, and ROI with real-time analytics.
                            </p>
                        </div>
                    </li>
                </ul>

                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: showGuidedTour ? '50px' : '0' }}>
                    <RegisterOrg
                        triggerRefresh={setTriggerRefresh}
                        showGuidedTour={showGuidedTour}
                        onGuidedTourClick={handleGuidedTourClick}
                        onRegistered={handleFirstOrgRegistered}
                    />
                </div>
            </div>
        </div>
    );

    const renderWelcomeModal = () => (
        <AppModal isOpen onClose={handleWelcomeGetStarted} width="md">
            <div style={welcomeModalStyles.body}>
                <div style={welcomeModalStyles.headerBlock}>
                    <LogoBadge size={56} />
                    <h1 style={welcomeModalStyles.title}>Welcome to AdVolt</h1>
                    <p style={welcomeModalStyles.subtitle}>
                        The intelligent <span style={{ color: colors.accent, fontWeight: 600 }}>real-world</span> advertising platform
                    </p>
                </div>
                <ul style={welcomeModalStyles.featureList}>
                    <li style={welcomeModalStyles.featureItem}>
                        <div style={welcomeModalStyles.featureIcon}>
                            <RocketIcon />
                        </div>
                        <span style={welcomeModalStyles.featureText}>Register your organization</span>
                    </li>
                    <li style={welcomeModalStyles.featureItem}>
                        <div style={welcomeModalStyles.featureIcon}>
                            <MagicIcon />
                        </div>
                        <span style={welcomeModalStyles.featureText}>Upload or let AI create engaging ads</span>
                    </li>
                    <li style={welcomeModalStyles.featureItem}>
                        <div style={welcomeModalStyles.featureIcon}>
                            <MapMarkedAltIcon />
                        </div>
                        <span style={welcomeModalStyles.featureText}>Create geo-targeted campaigns</span>
                    </li>
                    <li style={welcomeModalStyles.featureItemLast}>
                        <div style={welcomeModalStyles.featureIcon}>
                            <GlobeIcon />
                        </div>
                        <span style={welcomeModalStyles.featureText}>Reach your audience everywhere</span>
                    </li>
                </ul>
                <GradientButton onClick={handleWelcomeGetStarted} isBlock>
                    Get started
                </GradientButton>
            </div>
        </AppModal>
    );

    const expandedOrg = expandedOrgId ? organizations.find(o => o.id === expandedOrgId) : null;
    const compactOrgs = expandedOrgId ? organizations.filter(o => o.id !== expandedOrgId) : organizations;
    const stats = expandedOrgId && orgStats[expandedOrgId] ? orgStats[expandedOrgId] : { adsCount: 0, campaignsCount: 0 };

    const renderOrganizations = () => (
        <>
            {/* Expanded hero card */}
            {expandedOrg && (
                <div style={styles.expandedSection}>
                    <OrganizationHeroCard
                        organization={expandedOrg}
                        adsCount={stats.adsCount}
                        campaignsCount={stats.campaignsCount}
                        onEditClick={handleEditClick}
                        onDeleteClick={handleDeleteClick}
                        onResearchTriggered={handleResearchTriggered}
                        highlightResearch={highlightResearch}
                        onResearchTourClick={handleResearchTourAdvance}
                        onToggleBusinessSummary={() => fetchOrganizations()}
                        onCollapse={organizations.length > 1 ? handleCollapse : undefined}
                    />
                </div>
            )}

            {/* Compact cards grid */}
            {compactOrgs.length > 0 && (
                <div style={styles.compactGrid}>
                    {compactOrgs.map(function(org) {
                        return (
                            <OrganizationCard
                                key={org.id}
                                organization={org}
                                onClick={handleCardClick}
                            />
                        );
                    })}
                </div>
            )}
        </>
    );

    return (
        <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
            <div style={styles.container}>
                {/* Header - only show when organizations exist */}
                {organizations.length > 0 && (
                    <div className="org-page-header" style={styles.header}>
                        <div>
                            <h1 style={styles.title}>Organizations</h1>
                            <p style={styles.subtitle}>
                                {`${organizations.length} organization${organizations.length !== 1 ? 's' : ''} registered`}
                            </p>
                        </div>
                        <RegisterOrg triggerRefresh={setTriggerRefresh} />
                    </div>
                )}

                {/* Content */}
                {isLoading ? (
                    <div style={styles.loadingContainer}>
                        <p>Loading organizations...</p>
                    </div>
                ) : organizations.length === 0 ? (
                    renderEmptyState()
                ) : (
                    renderOrganizations()
                )}
            </div>

            {/* Edit Organization Modal */}
            <EditOrganization
                organization={selectedOrganization}
                isOpen={editModalOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
            />

            {/* Error Modal */}
            {showErrorModal && (
                <div style={styles.errorOverlay} onClick={() => setShowErrorModal(false)}>
                    <div style={styles.errorModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.errorIcon}>
                            <ExclamationCircleIcon style={{ fontSize: '24px' }} />
                        </div>
                        <h3 style={styles.errorTitle}>Error</h3>
                        <p style={styles.errorText}>{errorMessage}</p>
                        <button
                            style={styles.errorButton}
                            onClick={() => setShowErrorModal(false)}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Welcome Modal */}
            {showWelcomeModal && organizations.length === 0 && !isLoading && renderWelcomeModal()}
        </PageSection>
    );
}

export { Organizations };
